// slices/chat/addReferenceBlock.ts
import type { Dispatch } from 'redux';
import {
  addChatConversationBlock,
  type ChatAddBlockReference,
} from '../../services/chatConversations';
import type { ChatRootState } from './chatSelectors';
import { blockMessageAdded } from './chatSlice';

export interface AddReferenceBlockArgs {
  /** Client id of the answer that served the question block. */
  messageId: string;
  /** The served `question` block whose badge was tapped. */
  blockId: string;
  reference: ChatAddBlockReference;
}

export type AddReferenceBlockResult =
  { ok: true; messageId: string; blockId: string | null } | { ok: false };

/**
 * Study what a question block's badge points at, in the thread (ORA-546/547).
 *
 * The sibling of {@link addCitationBlock} for the other half of the same
 * route: the badge names the served question block and the kind of thing it
 * points at, and the server rebuilds the content from the student's own
 * scheduling rows — the client is trusted for ids and nothing else. The
 * response is the same block-only assistant message, so the slice hydrates it
 * the same way and the next turn's history sees the block as served.
 *
 * Idempotent on the server: a second tap on the same badge returns the block
 * the first one added, and `blockMessageAdded` recognises the message it
 * already holds, so nothing is duplicated in the thread.
 *
 * Resolves rather than throws, so the badge can re-enable itself and say what
 * happened.
 */
export const addReferenceBlock =
  ({ messageId, blockId, reference }: AddReferenceBlockArgs) =>
  async (
    dispatch: Dispatch,
    getState: () => ChatRootState
  ): Promise<AddReferenceBlockResult> => {
    const message = getState().chat.messages[messageId];
    if (!message?.serverId) return { ok: false };

    const { conversationId } = message;

    try {
      const added = await addChatConversationBlock(conversationId, {
        messageId: message.serverId,
        blockId,
        reference,
      });

      dispatch(
        blockMessageAdded({
          conversationId,
          afterMessageId: messageId,
          // A block-only message carries no attachments; the rest is already
          // in the read shape the slice hydrates from.
          message: {
            id: added.id,
            role: added.role,
            text: added.text,
            citations: added.citations,
            blocks: added.blocks,
          },
        })
      );

      return {
        ok: true,
        messageId: added.id,
        blockId: added.blocks[0]?.id ?? null,
      };
    } catch (caught) {
      console.error('Unable to add a chat block from a question badge', caught);
      return { ok: false };
    }
  };
